import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    const inlineTrigger = this.querySelector('.js-quick-add-inline-trigger');
    if (inlineTrigger) {
      inlineTrigger.addEventListener('click', this.handleClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    const inlineTrigger = this.querySelector('.js-quick-add-inline-trigger');
    if (inlineTrigger) {
      inlineTrigger.removeEventListener('click', this.handleClick);
    }
  }

  /**
   * Fetches product data and shows inline variants
   */
  fetchAndShowVariants = async () => {
    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */(gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.showInlineVariants(freshContent);
    }
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    if (this.classList.contains('quick-add--open')) {
      this.close();
    }
  };

  /**
   * Closes the quick add overlay
   */
  close() {
    const isMobile = isMobileBreakpoint();
    const existingOverlay = isMobile
      ? document.querySelector('.quick-add-variants-overlay.mobile-view')
      : this.closest('product-card')?.querySelector('.quick-add-variants-overlay');

    if (existingOverlay) {
      existingOverlay.classList.remove('active');
      setTimeout(() => {
        if (isMobile) {
          existingOverlay.remove();
        } else {
          if (!existingOverlay.classList.contains('active')) existingOverlay.style.display = 'none';
        }
      }, 300);
    }
    this.classList.remove('quick-add--open');
  }

  /**
   * Handles click on the quick add button
   */
  handleClick = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (this.classList.contains('quick-add--loading')) return;

    if (!this.classList.contains('quick-add--open')) {
      this.classList.add('quick-add--loading');
    }

    try {
      await this.fetchAndShowVariants();
    } finally {
      this.classList.remove('quick-add--loading');
    }
  };

  /**
   * Renders variant picker inline
   * @param {Element} productGrid - The product grid element
   */
  async showInlineVariants(productGrid) {
    const productCard = this.closest('product-card');
    if (!productCard) return;

    const cardGallery = /** @type {HTMLElement | null} */ (productCard.querySelector('.card-gallery'));
    if (!cardGallery) return;

    // Check if overlay already exists
    const isMobile = isMobileBreakpoint();

    if (isMobile) {
      // Mobile Logic: Toggle existing overlay in body or create new one
      const existingOverlay = document.querySelector('.quick-add-variants-overlay.mobile-view');

      // If we are already open, just close and return
      if (this.classList.contains('quick-add--open')) {
        this.close();
        return;
      }

      // If another quick add is open, close it first
      if (existingOverlay) {
        existingOverlay.classList.remove('active');
        setTimeout(() => existingOverlay.remove(), 300);
        const openComponent = document.querySelector('quick-add-component.quick-add--open');
        if (openComponent) openComponent.classList.remove('quick-add--open');
      }
    } else {
      // Desktop Logic: Toggle visibility within card
      let overlay = /** @type {HTMLElement | null} */ (cardGallery.querySelector('.quick-add-variants-overlay'));
      if (overlay) {
        if (!overlay.classList.contains('active')) {
          overlay.style.display = 'flex';
          overlay.classList.add('active');
          this.classList.add('quick-add--open');
        } else {
          this.close();
        }
        return;
      }
    }

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'quick-add-variants-overlay toous';

    if (isMobile) {
      overlay.classList.add('mobile-view');
      Object.assign(overlay.style, {
        position: 'fixed',
        height: 'auto',
        maxHeight: '80vh',
        bottom: '0',
        left: '0',
        right: '0',
        width: '100%',
        backgroundColor: 'var(--color-background)',
        zIndex: '2147483647',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        padding: 'var(--padding-sm)',
        margin: '0',
        overflowY: 'auto',
        boxShadow: '0 -5px 15px rgba(0,0,0,0.1)'
      });

      // --- Mobile Header Construction ---
      const header = document.createElement('div');
      Object.assign(header.style, {
        display: 'flex',
        gap: '12px',
        marginBottom: '10px',
        alignItems: 'center',
        paddingBottom: '10px',
        borderBottom: '1px solid var(--color-border, #e5e5e5)'
      });

      const productCard = this.closest('product-card');

      // Image
      const originalImg = productCard?.querySelector('img');
      if (originalImg) {
        const imgClone = originalImg.cloneNode(true);
        Object.assign(imgClone.style, {
          width: '60px',
          height: '60px',
          objectFit: 'cover',
          borderRadius: '4px',
          flexShrink: '0'
        });
        header.appendChild(imgClone);
      }

      // Info
      const infoDiv = document.createElement('div');
      infoDiv.style.flex = '1';

      // Title
      const title = document.createElement('div');
      title.textContent = this.dataset.productTitle || '';
      Object.assign(title.style, {
        fontWeight: 'bold',
        fontSize: '14px',
        lineHeight: '1.2',
        marginBottom: '4px'
      });
      infoDiv.appendChild(title);

      // Price
      const priceSource = productGrid.querySelector('product-price') || productCard?.querySelector('product-price');
      if (priceSource) {
        const priceClone = priceSource.cloneNode(true);
        // Ensure price is visible and styled simply
        if (priceClone instanceof HTMLElement) {
          priceClone.style.fontSize = '13px';
          priceClone.style.display = 'block';
        }
        infoDiv.appendChild(priceClone);
      }

      header.appendChild(infoDiv);

      // Close Button
      const closeButton = document.createElement('div');
      closeButton.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      Object.assign(closeButton.style, {
        marginLeft: 'auto',
        cursor: 'pointer',
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      });
      closeButton.addEventListener('click', () => {
        this.close();
      });
      header.appendChild(closeButton);

      overlay.appendChild(header);
      // ----------------------------------

      // Add a backdrop or close button could be nice here, but sticking to basics first.
    } else {
      Object.assign(overlay.style, {
        position: 'absolute',
        height: 'auto',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'var(--color-background)',
        zIndex: '20',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: 'var(--padding-sm)',
        margin: 'var(--padding-sm)',
        overflowY: 'auto'
      });
      // Initial state for desktop (created fresh) should be display: flex but hidden opacity
      // styles handle opacity, but display must be flex
      overlay.style.display = 'flex';
    }

    const productFormComponent = productGrid.querySelector('product-form-component');
    console.log('productFormComponent', productFormComponent);
    const variantPicker = productGrid.querySelector('variant-picker');

    // 1. Variant Picker (if separate)
    if (variantPicker && (!productFormComponent || !productFormComponent.contains(variantPicker))) {
      overlay.appendChild(variantPicker);
    }

    // 2. Fit Notes
    const fitNotesSource = this.querySelector('[data-fit-notes-content]');
    console.log('fitNotesSource', fitNotesSource);
    if (fitNotesSource && fitNotesSource.innerHTML.trim() !== '') {
      const fitNotesElement = document.createElement('div');
      fitNotesElement.className = 'quick-add__fit-notes';
      fitNotesElement.style.fontSize = '13px';
      fitNotesElement.style.marginBottom = 'var(--padding-sm)';
      fitNotesElement.style.marginTop = 'var(--padding-sm)';
      fitNotesElement.style.display = 'flex';
      fitNotesElement.style.alignItems = 'center';
      fitNotesElement.innerHTML = `<strong style="display:block; font-size: 10px; letter-spacing: 1.5px; font-weight: 600;">FIT NOTES:&nbsp;</strong> ${fitNotesSource.innerHTML}`;
      overlay.appendChild(fitNotesElement);
    }

    // 3. Product Form
    if (productFormComponent) {
      overlay.appendChild(productFormComponent);

      // Clean up the form display - hide everything except the buttons, hidden inputs, and picker
      const form = productFormComponent.querySelector('form');
      if (form) {
        Array.from(form.children).forEach(child => {
          // Keep hidden inputs and the buttons container
          const isInput = child.tagName === 'INPUT';
          const isAvail = child.classList.contains('product-form__buttons') ||
            child.classList.contains('product-form-buttons') ||
            child.tagName === 'ADD-TO-CART-COMPONENT-TOOUS' ||
            child.getAttribute('name') === 'add' ||
            child.tagName === 'VARIANT-PICKER';

          if (!isInput && !isAvail) {
            if (child instanceof HTMLElement) child.style.display = 'none';
          }
        });

        // Style the button
        const addToCartBtn = /** @type {HTMLElement | null} */ (form.querySelector('button[name="add"]'));
        if (addToCartBtn) {
          addToCartBtn.style.display = 'block';
          addToCartBtn.style.opacity = '1';
          addToCartBtn.style.width = '100%';
          addToCartBtn.style.marginTop = 'var(--padding-sm)';
        }
      }
    }

    if (isMobile) {
      document.body.appendChild(overlay);
      // Trigger animation
      requestAnimationFrame(() => {
        overlay.classList.add('active');
      });

    } else {
      cardGallery.appendChild(overlay);
      // Trigger animation for desktop too
      requestAnimationFrame(() => {
        overlay.classList.add('active');
      });
    }

    this.classList.add('quick-add--open');
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
